/**
 * Parse a URL to extract its components: base URL, hash path, and query
 * parameters. Automatically detects whether the URL uses hash-based routing or
 * standard routing.
 *
 * @param url - Optional URL string to parse. Defaults to window.location.href.
 * @returns an object containing baseUrl, hashPath, queryParams, and
 * isHashRouting.
 */
export function parseUrl(url?: string): {
  baseUrl: string;
  hashPath: string;
  queryParams: URLSearchParams;
  isHashRouting: boolean;
} {
  const currentUrl = url || window.location.href;
  const urlObject = new URL(currentUrl);

  let baseUrl: string;
  let hashPath: string;
  let hashQuery: string;
  const isHashRouting =
    urlObject.hash !== "" || currentUrl.split("#").length > 1;

  if (isHashRouting) {
    // Hash found - hash-based routing
    baseUrl = `${urlObject.origin}${urlObject.pathname}${urlObject.search}`;
    const hashPart = urlObject.hash.substring(1); // Remove leading '#'

    // Split hash into path and query parts
    const hashQueryIndex = hashPart.indexOf("?");
    if (hashQueryIndex !== -1) {
      hashPath = hashPart.substring(0, hashQueryIndex);
      hashQuery = hashPart.substring(hashQueryIndex + 1);
    } else {
      hashPath = hashPart;
      hashQuery = "";
    }
  } else {
    // No hash - standard routing
    baseUrl = `${urlObject.origin}${urlObject.pathname}`;
    hashPath = "";
    hashQuery = urlObject.search.substring(1); // Remove leading '?'
  }

  return {
    baseUrl,
    hashPath,
    queryParams: new URLSearchParams(hashQuery),
    isHashRouting,
  };
}

/**
 * Reconstruct a URL with query parameters. Uses the isHashRouting flag to
 * determine whether to use hash-based routing or standard routing.
 *
 * @param baseUrl - The base URL without hash or query params.
 * @param hashPath - The hash path (used with hash-based routing).
 * @param queryParams - URLSearchParams object with query parameters.
 * @param isHashRouting - Boolean flag indicating if hash-based routing is used.
 * @returns the reconstructed URL string.
 */
export function buildUrl(
  baseUrl: string,
  hashPath: string,
  queryParams: URLSearchParams,
  isHashRouting: boolean
): string {
  const queryString = queryParams.toString();

  if (isHashRouting) {
    // Hash-based routing: put query params inside the hash
    const newHash = queryString ? `${hashPath}?${queryString}` : hashPath;
    return `${baseUrl}#${newHash}`;
  } else {
    // Standard routing: put query params in the URL query string
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }
}
